import React from 'react';
import { useTranslation } from '../../hooks/useTranslation';

const JourneyOverview = ({
  journeyData,
  progress,
  onStartWeek,
  friend,
  loading,
  onBack,
  personalization = null,
  onRefreshPersonalization,
  personalizationLoading = false,
  personalizationError = null,
  hasReflectionsForPersonalization = false
}) => {
  const { t } = useTranslation();
  const weeks = [
    {
      id: 'week1',
      title: t('week1Title'),
      focus: friend ? t('week1FocusWithFriend', { name: friend.name }) : t('week1Focus'),
      activities: friend ? [
        t('activityFriendshipFitWithFriend', { name: friend.name }),
        t('activityClarifyIntention'),
        t('activityCraftMessage')
      ] : [t('activityFriendshipFit'), t('activityClarifyIntention'), t('activityCraftMessage')],
      color: '#ff6b6b'
    },
    {
      id: 'week2',
      title: t('week2Title'),
      focus: friend ? t('week2FocusWithFriend', { name: friend.name }) : t('week2Focus'),
      activities: friend ? [
        t('activityReadResponseWithFriend', { name: friend.name }),
        t('activityMicroConnection'),
        t('activityBalanceCheck')
      ] : [t('activityReadResponse'), t('activityMicroConnection'), t('activityBalanceCheck')],
      color: '#4ecdc4'
    },
    {
      id: 'week3',
      title: t('week3Title'),
      focus: friend ? t('week3FocusWithFriend', { name: friend.name }) : t('week3Focus'),
      activities: friend ? [
        t('activityGoDeeperWithFriend', { name: friend.name }),
        t('activityShareTruth'),
        t('activityDeepListening')
      ] : [t('activityGoDeeper'), t('activityShareTruth'), t('activityDeepListening')],
      color: '#45b7d1'
    },
    {
      id: 'week4',
      title: t('week4Title'),
      focus: friend ? t('week4FocusWithFriend', { name: friend.name }) : t('week4Focus'),
      activities: friend ? [
        t('activityDesignRitualWithFriend', { name: friend.name }),
        t('activityRestartRule'),
        t('activityFinalIntegration')
      ] : [t('activityDesignRitual'), t('activityRestartRule'), t('activityFinalIntegration')],
      color: '#96ceb4'
    }
  ];

  const getProgressPercentage = (weekProgress) => {
    return (weekProgress.completed / weekProgress.total) * 100;
  };

  const getOverallProgress = () => {
    const totalCompleted = Object.values(progress).reduce((sum, week) => sum + week.completed, 0);
    const totalActivities = Object.values(progress).reduce((sum, week) => sum + week.total, 0);
    return (totalCompleted / totalActivities) * 100;
  };

  if (loading) {
    return (
      <div className="journey-overview">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>{t('loadingPersonalizedJourney')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="journey-overview">
      {onBack && (
        <div className="journey-header">
          <button className="back-btn" onClick={onBack}>
            ←
          </button>
        </div>
      )}
      <div className="overview-hero">
        <div className="hero-content">
          <h1 className="journey-main-title">{journeyData.title}</h1>
          <p className="journey-description">{journeyData.description}</p>

          {onRefreshPersonalization && (
            <div className="journey-personalization-card">
              <div className="journey-personalization-header">
                <h2 className="journey-personalization-title">{t('personalizationForYou')}</h2>
                <button
                  type="button"
                  className="btn btn-secondary journey-personalization-btn"
                  onClick={() => onRefreshPersonalization()}
                  disabled={personalizationLoading || !hasReflectionsForPersonalization}
                >
                  {personalizationLoading ? t('personalizationUpdating') : t('personalizationRefresh')}
                </button>
              </div>
              {!hasReflectionsForPersonalization && (
                <p className="journey-personalization-hint">{t('personalizationNeedReflections')}</p>
              )}
              {personalizationError && (
                <p className="journey-personalization-error" role="alert">{personalizationError}</p>
              )}
              {personalization?.overviewEncouragement && (
                <p className="journey-personalization-body">{personalization.overviewEncouragement}</p>
              )}
              {Array.isArray(personalization?.themes) && personalization.themes.length > 0 && (
                <div className="journey-personalization-themes">
                  <span className="journey-personalization-themes-label">{t('personalizationThemesHeading')}</span>
                  <ul>
                    {personalization.themes.map((theme, i) => (
                      <li key={i}>{theme}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          <div className="friend-progress-bar">
            {friend && (
              <div className="friend-info-section">
                <div className="friend-avatar">
                  {friend.relationship === 'friend' ? '👫' : 
                   friend.relationship === 'best-friend' ? '💕' :
                   friend.relationship === 'colleague' ? '👔' :
                   friend.relationship === 'family' ? '👨‍👩‍👧‍👦' :
                   friend.relationship === 'acquaintance' ? '👋' :
                   friend.relationship === 'mentor' ? '🧠' :
                   friend.relationship === 'neighbor' ? '🏠' : '👫'}
                </div>
                <div className="friend-details">
                  <h3>{friend.name}</h3>
                  <p className="friend-relationship">
                    {friend.relationship.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </p>
                </div>
              </div>
            )}
            
            <div className={`progress-section ${!friend ? 'progress-section-full' : ''}`}>
              <div className="progress-header">
                <span className="progress-label">{t('overallProgress')}</span>
                <span className="progress-percentage">{Math.round(getOverallProgress())}%</span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ 
                    width: `${getOverallProgress()}%`
                  }}
                ></div>
              </div>
              <p className="progress-text">
                {Object.values(progress).reduce((sum, week) => sum + week.completed, 0)} {t('of')}{' '}
                {Object.values(progress).reduce((sum, week) => sum + week.total, 0)} {t('activitiesCompleted')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="objectives-section">
        <h2>{t('journeyObjectives')}</h2>
        <div className="objectives-grid">
          {journeyData.objectives.map((objective, index) => (
            <div key={index} className="objective-card">
              <div className="objective-icon">🎯</div>
              <p>{objective}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="weeks-section">
        <h2>{t('your4WeekJourney')}</h2>
        <div className="weeks-grid">
          {weeks.map((week, index) => {
            const weekProgress = progress[week.id];
            // Week is completed if completed >= total (handles edge cases where completed exceeds total)
            const isCompleted = weekProgress.completed >= weekProgress.total;
            // Week is unlocked if it's the first week OR if the previous week is completed
            const isUnlocked = index === 0 || progress[weeks[index - 1].id].completed >= progress[weeks[index - 1].id].total;
            
            return (
              <div 
                key={week.id}
                className={`week-card ${isCompleted ? 'completed' : ''} ${!isUnlocked ? 'locked' : ''}`}
                onClick={() => isUnlocked && onStartWeek(week.id)}
              >
                <div className="week-header">
                  <div className="week-number">
                    {index + 1}
                  </div>
                  <div className="week-info">
                    <h3>{week.title}</h3>
                  </div>
                  {isCompleted && <div className="completion-badge">✓</div>}
                </div>
                
                <div className="week-progress">
                  <div className="progress-details">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ 
                          width: `${getProgressPercentage(weekProgress)}%`
                        }}
                      ></div>
                    </div>
                    <span className="progress-text">
                      {weekProgress.completed}/{weekProgress.total} {t('activities')} ({Math.round(getProgressPercentage(weekProgress))}%)
                    </span>
                  </div>
                </div>

                <div className="week-activities">
                  <p className="week-focus-text">{week.focus}</p>
                  {personalization?.weekFocusNotes?.[week.id] && (
                    <p className="week-focus-personalized">{personalization.weekFocusNotes[week.id]}</p>
                  )}
                </div>

                <div className="week-action">
                  {isCompleted ? (
                    <span className="action-completed">✓ {t('completed')}</span>
                  ) : isUnlocked ? (
                    <span className="action-start">{t('startWeek')} {index + 1}</span>
                  ) : (
                    <span className="action-locked">{t('completeWeekFirst', { week: index })}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {(() => {
        const totalCompleted = Object.values(progress).reduce((sum, week) => sum + week.completed, 0);
        const totalActivities = Object.values(progress).reduce((sum, week) => sum + week.total, 0);
        const overallProgress = getOverallProgress();
        
        // Find the current week (first incomplete week)
        let currentWeek = null;
        let nextWeek = null;
        
          for (let i = 0; i < weeks.length; i++) {
            const weekProgress = progress[weeks[i].id];
            // Week is completed if completed >= total (handles edge cases where completed exceeds total)
            const isCompleted = weekProgress.completed >= weekProgress.total;
            const isUnlocked = i === 0 || progress[weeks[i - 1].id].completed >= progress[weeks[i - 1].id].total;
          
          if (!isCompleted && isUnlocked) {
            currentWeek = weeks[i];
            break;
          } else if (!isCompleted && !isUnlocked) {
            nextWeek = weeks[i];
            break;
          }
        }
        
        // Check if all weeks are completed
        const allCompleted = weeks.every(week => {
          const weekProgress = progress[week.id];
          return weekProgress.completed >= weekProgress.total;
        });
        
        if (allCompleted) {
          return (
            <div className="getting-started">
              <h3>{t('journeyComplete')}</h3>
              <p>{t('journeyCompleteMessage', { total: totalActivities })}</p>
              <button 
                className="btn btn-secondary start-journey-btn"
                onClick={() => onStartWeek('week1')}
              >
                {t('reviewJourney')}
              </button>
            </div>
          );
        } else if (currentWeek) {
          const weekIndex = weeks.findIndex(w => w.id === currentWeek.id);
          return (
            <div className="getting-started">
              <h3>{t('continueYourJourney')}</h3>
              <p>You're making great progress! Continue with {currentWeek.title} to keep building your friendship.</p>
              <button 
                className="btn btn-primary start-journey-btn"
                onClick={() => onStartWeek(currentWeek.id)}
              >
                {t('continueWeek', { weekTitle: currentWeek.title })}
              </button>
            </div>
          );
        } else if (nextWeek) {
          const prevWeekIndex = weeks.findIndex(w => w.id === nextWeek.id) - 1;
          const prevWeek = weeks[prevWeekIndex];
          return (
            <div className="getting-started">
              <h3>{t('completeWeekFirst', { week: prevWeek.title })}</h3>
              <p>{t('completeWeekFirstMessage', { week: prevWeek.title, nextWeek: nextWeek.title })}</p>
              <button 
                className="btn btn-primary start-journey-btn"
                onClick={() => onStartWeek(prevWeek.id)}
              >
                {t('goToWeek', { week: prevWeek.title })}
              </button>
            </div>
          );
        } else {
          return (
            <div className="getting-started">
              <h3>{t('readyToBegin')}</h3>
              <p>{t('readyToBeginMessage')}</p>
              <button 
                className="btn btn-primary start-journey-btn"
                onClick={() => onStartWeek('week1')}
              >
                {t('beginWeek1')}
              </button>
            </div>
          );
        }
      })()}
    </div>
  );
};

export default JourneyOverview;